import React from 'react';
import { Badge, type BadgeProps } from '@/design-system/badge';
import { PackageStatus } from '@/services/api/packages';
import {
  ITEM_CONDITION_LABELS,
  ITEM_STATUS_LABELS,
  type ItemCondition,
} from '@/services/api/status';

type BadgeStatus = NonNullable<BadgeProps['status']>;

// Client-facing wording for the item vocabulary: this page is read by clients,
// so "At Hub" beats the internal "Hub scanned". Everything else follows the
// shared labels. Legacy values are kept so pre-migration rows still read.
export const STATUS_LABELS: Partial<Record<PackageStatus, string>> = {
  ...ITEM_STATUS_LABELS,
  HUB_SCANNED: 'At Hub',
  HANDOVER_SCANNED: 'Handover Scanned',
  IN_DELIVERY: 'In Delivery',
  IN_RETURN: 'In Return',
  RETURNED_TO_HUB: 'Returned to Hub',
  RETURNED_TO_CLIENT: 'Returned to Client',
};

// Maps each package (item) status to a kit Badge status. Kept in its own file
// so both the list and the detail drawer import it without a circular dep.
export const STATUS_TONE: Partial<Record<PackageStatus, BadgeStatus>> = {
  CREATED: 'verified',
  HUB_SCANNED: 'information',
  HANDOVER_SCANNED: 'away',
  IN_DELIVERY: 'feature',
  COMPLETED: 'success',
  IN_RETURN: 'warning',
  RETURNED_TO_HUB: 'error',
  RETURNED_TO_CLIENT: 'error',
  CANCELLED: 'neutral',
  LOST: 'error',
  // Legacy (pre-migration rows)
  DISPATCHED: 'feature',
  DELIVERED: 'success',
  RETURNED: 'error',
  DAMAGED: 'warning',
  FAILED: 'error',
};

export const PackageStatusBadge: React.FC<{ status: PackageStatus }> = ({
  status,
}) => (
  <Badge status={STATUS_TONE[status] ?? 'neutral'} type="dot">
    {STATUS_LABELS[status] ?? status}
  </Badge>
);

/**
 * Condition flag — the v2 attribute that replaced status `DAMAGED`. Rendered
 * next to the status badge, never in place of it. Nothing for `OK` / unset.
 */
export const PackageConditionBadge: React.FC<{
  condition?: ItemCondition | null;
}> = ({ condition }) =>
  condition === 'DAMAGED' ? (
    <Badge
      status="warning"
      size="sm"
      type="left-icon"
      icon={<i className="ri-alert-line" aria-hidden="true" />}
    >
      {ITEM_CONDITION_LABELS.DAMAGED}
    </Badge>
  ) : null;
